import { FC, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import './remark.animate.scss';
import { useScrollAnimation } from './UseScrollAnimation';

interface RemarksRotatorProps {
    items: any[];
    lastIndex?: number;
    globalStartTime: number;
    mainFlightNumber?: string;
    cssClass?: string;
    itemHeight?: number;
}

const RemarksRotator: FC<RemarksRotatorProps> = ({
    cssClass,
    items,
    lastIndex,
    globalStartTime,
    mainFlightNumber,
    itemHeight: itemHeightProp,
}) => {
    const shouldRotate = items.length > 1;
    const measureRef = useRef<HTMLLIElement>(null);
    const [autoHeight, setAutoHeight] = useState<number | null>(null);

    const measure = useCallback(() => {
        const el = measureRef.current;
        if (!el) return;
        // Temporarily remove fixed height to measure natural content height
        const prev = el.style.height;
        const prevLh = el.style.lineHeight;
        el.style.height = 'auto';
        el.style.lineHeight = 'normal';
        const h = el.scrollHeight;
        el.style.height = prev;
        el.style.lineHeight = prevLh;
        if (h > 0) setAutoHeight(h);
    }, []);

    useEffect(() => {
        const raf = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', measure);
        };
    }, [measure, items]);

    const itemHeight = itemHeightProp ?? autoHeight ?? 50;

    const itemsHash = useMemo(() => {
        return items.map((item, index) => `${index}:${item || 'empty'}`).join('|');
    }, [items]);

    const displayItems = shouldRotate ? [...items, ...items] : items;

    const { listRef } = useScrollAnimation({
        items: displayItems,
        globalStartTime,
        itemHeight,
        scrollDuration: 1.5,
        animationPrefix: 'remarks',
        enabled: shouldRotate
    });

    return (
        <div
            className={`scroll-wrapper ${cssClass ?? ''}`}
            style={{ height: `${itemHeight}px` }}
            key={`${itemsHash}-${itemHeight}`}
        >
            {shouldRotate ? (
                <ul className="scroll-item-list" ref={listRef}>
                    {displayItems.map((text, i) => {
                        const originalLength = items.length;
                        const isLastInOriginalSequence = lastIndex !== undefined &&
                            (i % originalLength) === lastIndex;

                        const actualIndex = i % originalLength;
                        const isDuplicate = i >= originalLength;
                        const keyPrefix = isDuplicate ? 'dup' : 'orig';
                        const flightPrefix = mainFlightNumber ? `${mainFlightNumber}-` : '';
                        const uniqueKey = `${flightPrefix}remarks-${keyPrefix}-${actualIndex}-${text || 'empty'}`;

                        return (
                            <li
                                ref={i === 0 ? measureRef : undefined}
                                style={{ height: `${itemHeight}px`, lineHeight: `${itemHeight}px` }}
                                className={`scroll-item ${isLastInOriginalSequence ? "underline" : ""}`}
                                key={uniqueKey}
                            >
                                {text}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <ul className="scroll-item-list">
                    {displayItems.map((text, i) => {
                        const flightPrefix = mainFlightNumber ? `${mainFlightNumber}-` : '';
                        const uniqueKey = `${flightPrefix}remarks-static-${i}-${text || 'empty'}`;

                        return (
                            <li
                                ref={i === 0 ? measureRef : undefined}
                                style={{ height: `${itemHeight}px`, lineHeight: `${itemHeight}px` }}
                                className={`scroll-item ${lastIndex === i ? "underline" : ""}`}
                                key={uniqueKey}
                            >
                                {text}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default RemarksRotator;